import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { SKIP_TOKEN_REFRESH_CHECKER } from '@root/app/core/auth/skip-token-refresh-checker-context.const';
import { map, Observable, of, takeUntil } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { AuthStoreService } from '../auth-store.service';
import { environment } from '@root/environments/environment';
import { authDebugLog } from '@core/auth/utils/auth-log.util';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private readonly authStoreService = inject(AuthStoreService);
  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    request = request.clone({
      setHeaders: {
        pragma: 'no-cache',
        'cache-control': 'no-cache',
      },
    });

    const url = request['url'].toString().toLowerCase();
    const isLoginRequest = url.includes('token');

    const isAPIRequest =
      url.startsWith(environment.apiUrl) || url.includes('api');

    const skipCheckingRefreshToken =
      !isAPIRequest ||
      (isAPIRequest && request.context.get(SKIP_TOKEN_REFRESH_CHECKER)) ||
      !!this.authStoreService.getBypassedAccessToken();

    const tokenRefreshChecker = skipCheckingRefreshToken
      ? of(true)
      : this.authStoreService.refreshTokenBeforeRequest(request.url);

    return tokenRefreshChecker.pipe(
      tap(() => {
        if (this.authStoreService.$refreshTokenInProgress()) {
          authDebugLog(
            `current url ${url} won't wait for refresh token request`,
            {
              priority: 'info',
              emphasis: 'italic',
            }
          );
        }
      }),
      map(() => this.authStoreService.$tokenInfo()),
      tap((tokenInfo) => {
        if (!isAPIRequest) return;
        if (tokenInfo?.accessToken) {
          request = request.clone({
            setHeaders: {
              Authorization: `Bearer ${tokenInfo.accessToken}`,
            },
          });
        } else if (isLoginRequest && request.method === 'GET') {
          request = request.clone({ withCredentials: true });
        }
      }),
      switchMap(() => {
        return next.handle(request).pipe(
          tap((res) => {
            if (
              res instanceof HttpResponse &&
              request.context.get(SKIP_TOKEN_REFRESH_CHECKER)
            ) {
              const serverDate = res.headers.get('Date');
              if (serverDate) {
                this.authStoreService.setRelativeTokenShiftTime(
                  new Date(serverDate)
                );
              }
            }
          })
        );
      }),
      /**
       * once a user logged out, cancel all ongoing requests
       */
      takeUntil(this.authStoreService.logout$)
    );
  }
}
